import { useCallback, RefObject } from 'react';
import usePDA1080_R01Store from "./usePDA1080_R01Store";
import { scanLot, returnMaterial } from "src/apis/materialReturnApi";
import { useToast } from "src/hooks/useToast";
import { PDA1080R01GridData } from "./PDA1080_R01Dto";

/**
 * PDA1080_R01 Content Hook
 * 
 * Container에서 사용할 비즈니스 로직을 제공합니다.
 * - LOT 스캔
 * - 그리드 추가/삭제
 * - 전체 반입 등록
 */
export const usePDA1080_R01Content = (lotInputRef?: RefObject<HTMLInputElement>) => {
  const { 
    lotNo,
    setLotNo,
    setScannedLot,
    scannedLot,
    returnQty,
    setReturnQty,
    setLoading,
    returnItems,
    addReturnItem,
    removeReturnItem,
    clearReturnItems,
  } = usePDA1080_R01Store();
  
  const { showToast } = useToast();

  /**
   * LOT 스캔 - 자재 정보 조회
   */
  const handleScanLot = useCallback(async () => {
    if (!lotNo.trim()) {
      showToast("error", "LOT 번호를 입력하세요.");
      return;
    }

    try {
      setLoading(true);
      
      const response = await scanLot(lotNo.trim());
      const lotData = response?.data?.data;
      
      if (!lotData) {
        throw new Error("LOT 정보를 찾을 수 없습니다.");
      }
      
      setScannedLot(lotData);
      setReturnQty(lotData.lotQty); // LOT 수량 자동 세팅
      showToast("success", "LOT 스캔 완료");
      setTimeout(() => lotInputRef?.current?.focus(), 50);
      
    } catch (error: any) {
      console.error("LOT 스캔 실패:", error);
      showToast("error", error?.response?.data?.message || "LOT 스캔에 실패했습니다.");
      setLotNo("");        // 오류 시 LOT 번호 초기화
      setScannedLot(null);
      setTimeout(() => lotInputRef?.current?.focus(), 50);
    } finally {
      setLoading(false);
    }
  }, [lotNo, setLoading, setScannedLot, setReturnQty, setLotNo, showToast, lotInputRef]);

  /**
   * 그리드에 추가 (반입 대기 목록)
   */
  const handleAddToGrid = useCallback(() => {
    if (!scannedLot) {
      showToast("error", "LOT 정보가 없습니다. LOT를 먼저 스캔하세요.");
      return;
    }

    if (returnQty <= 0) {
      showToast("error", "반입 수량은 0보다 커야 합니다.");
      return;
    }

    // 중복 체크 (같은 LOT + 품목 조합)
    const isDuplicate = returnItems.some(
      item => item.lotNo === scannedLot.lotNo && item.itemCd === scannedLot.itemCd
    );
    
    if (isDuplicate) {
      showToast("error", "이미 추가된 LOT입니다.");
      return;
    }

    // 그리드에 추가할 데이터
    const gridData: PDA1080R01GridData = {
      lotNo: scannedLot.lotNo,
      itemCd: scannedLot.itemCd,
      itemNm: scannedLot.itemNm,
      itemSpec: scannedLot.itemSpec,
      returnQty: returnQty,
      locationCd: scannedLot.locationCd,
      warehouseCd: scannedLot.warehouseCd,
      warehouseNm: scannedLot.warehouseNm,
      itemStatus: scannedLot.itemStatus,
      workOrderNo: scannedLot.workOrderNo,
    };

    addReturnItem(gridData);
    showToast("success", `반입 대기 추가 (${returnItems.length + 1}건)`);

    // 입력 초기화 (다음 LOT 스캔 준비)
    setLotNo("");
    setScannedLot(null);
    setReturnQty(0);

    // 저장 후 LOT 입력에 포커스
    setTimeout(() => lotInputRef?.current?.focus(), 50);
  }, [scannedLot, returnQty, returnItems, addReturnItem, setLotNo, setScannedLot, setReturnQty, showToast, lotInputRef]);

  /**
   * 그리드에서 항목 삭제
   */
  const handleRemoveFromGrid = useCallback((lotNo: string, itemCd: string) => {
    removeReturnItem(lotNo, itemCd);
    showToast("info", "반입 대기 목록에서 삭제되었습니다.");
  }, [removeReturnItem, showToast]);

  /**
   * 전체 반입 등록 (일괄 처리)
   */
  const handleSaveAll = useCallback(async () => {
    if (returnItems.length === 0) {
      showToast("error", "반입할 항목이 없습니다.");
      return;
    }

    try {
      setLoading(true);

      // 각 항목별로 반입 API 호출
      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      for (const item of returnItems) {
        try {
          await returnMaterial({
            lotNo: item.lotNo,
            itemCd: item.itemCd,
            returnQty: item.returnQty,
            warehouseCd: item.warehouseCd,
            logicCenter: "", // 백엔드에서 자동 획득
            locationCd: item.locationCd,
            itemStatus: item.itemStatus,
            workOrderNo: item.workOrderNo,
          });
          successCount++;
        } catch (error: any) {
          console.error(`반입 실패 (LOT: ${item.lotNo}):`, error);
          failCount++;
          errors.push(`${item.lotNo}: ${error?.response?.data?.message || '실패'}`);
        }
      }

      // 결과 메시지
      if (successCount > 0 && failCount === 0) {
        const totalQty = returnItems.reduce((sum, item) => sum + item.returnQty, 0);
        showToast("success", `자재반입 완료 (${successCount}건, 총 ${totalQty.toLocaleString()}개)`);
        clearReturnItems();
      } else if (successCount > 0 && failCount > 0) {
        showToast("warning", `일부 반입 완료 (성공: ${successCount}건, 실패: ${failCount}건)\n${errors.join('\n')}`);
      } else {
        showToast("error", `자재반입 실패\n${errors.join('\n')}`);
      }

    } catch (error: any) {
      console.error("자재반입 실패:", error);
      showToast("error", error?.response?.data?.message || "자재반입에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [returnItems, setLoading, clearReturnItems, showToast]);

  /**
   * 취소 (입력 초기화)
   */
  const handleCancel = useCallback(() => {
    if (returnItems.length > 0) {
      const confirm = window.confirm(
        `반입 대기중인 ${returnItems.length}건의 항목이 있습니다.\n모두 취소하시겠습니까?`
      );
      if (!confirm) {
        return;
      }
      clearReturnItems();
      showToast("info", "반입 대기 목록이 초기화되었습니다.");
    }
    
    setLotNo("");
    setScannedLot(null);
    setReturnQty(0);
  }, [returnItems, clearReturnItems, setLotNo, setScannedLot, setReturnQty, showToast]);

  /**
   * Enter 키 이벤트 핸들러
   */
  const handleLotNoEnter = useCallback(() => {
    handleScanLot();
  }, [handleScanLot]);

  const handleQtyEnter = useCallback(() => {
    handleAddToGrid();
  }, [handleAddToGrid]);

  return {
    handleScanLot,
    handleAddToGrid,
    handleRemoveFromGrid,
    handleSaveAll,
    handleCancel,
    handleLotNoEnter,
    handleQtyEnter,
  };
};
